import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';

import { otpAuthAPI } from '../api/authAPI';
import { IRegisterClient } from '../types/Auth';
import { startSession } from '../libs/session';
import { toOtpAuthError } from '../libs/otpAuthError';
import { setAuth } from '../../../store/slices/userSlice';
import { userInfo } from '../../../store/selectors/user';
import { custonSooner } from '../../../components/CustopmSooner';
import { captureApiError } from '../../../lib/sentry-utils';
import { toApiPhone } from '../../../utils/formatPhone';

/**
 * Завершение регистрации: номер, имя и тот же код, что проверил `verify-otp`.
 *
 * Именно здесь появляется аккаунт, поэтому шаг с именем обязательный — до него
 * клиента в базе нет. Повторный вызов на уже зарегистрированный номер
 * безопасен: бэкенд не дублирует аккаунт, а возвращает существующий с
 * `is_new: false` — для приложения это обычный вход.
 */
export const useRegisterClient = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const user = useSelector(userInfo);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({ phone, name, otp }: IRegisterClient) => {
      const apiPhone = toApiPhone(phone);
      const res = await otpAuthAPI.register({ phone: apiPhone, name, otp });
      const data = res.data.data;

      startSession(data.token, queryClient);

      return data;
    },
    onSuccess: () => {
      dispatch(setAuth({ ...user, isAuth: true }));
    },
    onError: error => {
      captureApiError(error, '/client/otp-auth/register', 'POST');
      custonSooner.error(toOtpAuthError(error).message);
    },
  });

  return {
    registerClient: mutateAsync,
    isPending,
  };
};
